import React from 'react';
import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X } from 'lucide-react';
import { Frequency, Schedule, Debt } from '../types';

interface DebtModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  householdId: string;
  editingDebt?: Debt | null;
  initialDebtType?: 'loan' | 'credit_card' | 'mortgage' | 'line_of_credit';
}

export default function DebtModal({ isOpen, onClose, userId, householdId, editingDebt, initialDebtType }: DebtModalProps) {
  const [name, setName] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [currentAmount, setCurrentAmount] = useState('');
  const [monthlyMinimum, setMonthlyMinimum] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [debtType, setDebtType] = useState<'loan' | 'credit_card' | 'mortgage' | 'line_of_credit'>(initialDebtType || 'loan');
  const [statementDate, setStatementDate] = useState('1');
  
  const [totalMonths, setTotalMonths] = useState('');
  const [remainingMonths, setRemainingMonths] = useState('');
  const [amortizationYears, setAmortizationYears] = useState('');
  const [termYears, setTermYears] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [maturityDate, setMaturityDate] = useState('');
  const [expectedRenewalRate, setExpectedRenewalRate] = useState('');
  
  const [homeValue, setHomeValue] = useState('');
  const [isInvestmentLoan, setIsInvestmentLoan] = useState(false);
  const [paymentMatchHint, setPaymentMatchHint] = useState('');
  
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [date1, setDate1] = useState('1');
  const [date2, setDate2] = useState('15');
  const [nextDate, setNextDate] = useState(() => new Date().toISOString().split('T')[0]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingDebt && isOpen) {
      setName(editingDebt.name);
      setTotalAmount(editingDebt.totalAmount.toString());
      setCurrentAmount(editingDebt.currentAmount.toString());
      setMonthlyMinimum(editingDebt.monthlyMinimum.toString());
      setInterestRate(editingDebt.interestRate.toString());
      setDebtType(editingDebt.type || 'loan');
      if (editingDebt.statementDate) setStatementDate(editingDebt.statementDate.toString());
      if (editingDebt.totalMonths) setTotalMonths(editingDebt.totalMonths.toString());
      if (editingDebt.remainingMonths) setRemainingMonths(editingDebt.remainingMonths.toString());
      if (editingDebt.amortizationMonths) setAmortizationYears((editingDebt.amortizationMonths / 12).toString());
      if (editingDebt.termMonths) setTermYears((editingDebt.termMonths / 12).toString());
      if (editingDebt.issueDate) setIssueDate(editingDebt.issueDate);
      if (editingDebt.maturityDate) setMaturityDate(editingDebt.maturityDate);
      if (editingDebt.expectedRenewalRate) setExpectedRenewalRate(editingDebt.expectedRenewalRate.toString());
      if (editingDebt.homeValue) setHomeValue(editingDebt.homeValue.toString());
      if (editingDebt.isInvestmentLoan !== undefined) setIsInvestmentLoan(editingDebt.isInvestmentLoan);
      setPaymentMatchHint(editingDebt.paymentMatchHint || '');
      setFrequency(editingDebt.schedule.frequency);
      if (editingDebt.schedule.date1) setDate1(editingDebt.schedule.date1.toString());
      if (editingDebt.schedule.date2) setDate2(editingDebt.schedule.date2.toString());
      if (editingDebt.schedule.nextDate) {
        try {
          setNextDate(editingDebt.schedule.nextDate.toISOString().split('T')[0]);
        } catch(e) { }
      }
    } else if (isOpen) {
      setName('');
      setTotalAmount('');
      setCurrentAmount('');
      setMonthlyMinimum('');
      setInterestRate('');
      setDebtType(initialDebtType || 'loan');
      setStatementDate('1');
      setTotalMonths('');
      setRemainingMonths('');
      setAmortizationYears('');
      setTermYears('');
      setIssueDate('');
      setMaturityDate('');
      setExpectedRenewalRate('');
      setHomeValue('');
      setIsInvestmentLoan(false);
      setPaymentMatchHint('');
      setFrequency('monthly');
      setDate1('1');
      setDate2('15');
      setNextDate(new Date().toISOString().split('T')[0]);
    }
  }, [editingDebt, isOpen]);

  // Auto-calculate Canadian minimum payment for credit cards and lines of credit
  useEffect(() => {
    if (debtType === 'credit_card' || debtType === 'line_of_credit') {
      const balance = currentAmount === '' ? Number(totalAmount) : Number(currentAmount);
      const rate = Number(interestRate) || 0;
      if (balance > 0) {
        const monthlyInterest = balance * (rate / 100) / 12;
        let calcMin = monthlyInterest + 10;
        if (calcMin > balance) calcMin = balance;
        setMonthlyMinimum(calcMin.toFixed(2));
      } else {
        setMonthlyMinimum('0');
      }
    }
  }, [debtType, currentAmount, totalAmount, interestRate]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totalAmount || !monthlyMinimum || !interestRate) return;
    
    setLoading(true);
    try {
      const schedule: Schedule = { frequency };
      if (debtType === 'credit_card' || debtType === 'line_of_credit') {
        schedule.frequency = 'monthly';
        // Estimate due date as 21 days after statement date
        const stDate = Number(statementDate) || 1;
        schedule.date1 = (stDate + 21) % 30 || 30;
      } else if (frequency === 'monthly') {
        schedule.date1 = Number(date1) || 1;
      } else if (frequency === 'semi-monthly') {
        schedule.date1 = Number(date1) || 1;
        schedule.date2 = Number(date2) || 15;
      } else {
        const [year, month, day] = nextDate.split('-');
        schedule.nextDate = new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0);
      }

      const debtData = {
        userId,
        householdId,
        type: debtType,
        name,
        totalAmount: Number(totalAmount),
        currentAmount: currentAmount === '' ? ((debtType === 'credit_card' || debtType === 'line_of_credit') ? 0 : Number(totalAmount)) : Number(currentAmount),
        monthlyMinimum: Number(monthlyMinimum),
        schedule,
        statementDate: (debtType === 'credit_card' || debtType === 'line_of_credit') ? Number(statementDate) : null,
        autoCalculateMin: debtType === 'credit_card' || debtType === 'line_of_credit',
        interestRate: Number(interestRate),
        totalMonths: debtType === 'loan' && totalMonths ? Number(totalMonths) : null,
        remainingMonths: (debtType === 'loan' || debtType === 'mortgage') && remainingMonths ? Number(remainingMonths) : null,
        amortizationMonths: debtType === 'mortgage' && amortizationYears ? Number(amortizationYears) * 12 : null,
        termMonths: debtType === 'mortgage' && termYears ? Number(termYears) * 12 : null,
        issueDate: (debtType === 'loan' || debtType === 'mortgage') && issueDate ? issueDate : null,
        maturityDate: (debtType === 'loan' || debtType === 'mortgage') && maturityDate ? maturityDate : null,
        expectedRenewalRate: debtType === 'mortgage' && expectedRenewalRate ? Number(expectedRenewalRate) : null,
        homeValue: debtType === 'mortgage' && homeValue ? Number(homeValue) : null,
        isInvestmentLoan: (debtType === 'line_of_credit' || debtType === 'loan') ? isInvestmentLoan : false,
        paymentMatchHint: (debtType === 'loan' || debtType === 'mortgage') && paymentMatchHint.trim() ? paymentMatchHint.trim() : null,
      };

      if (editingDebt?.id) {
        await updateDoc(doc(db, 'debts', editingDebt.id), debtData);
      } else {
        await addDoc(collection(db, 'debts'), {
          ...debtData,
          createdAt: serverTimestamp()
        });
      }
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative my-auto max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold mb-6 text-slate-900">{editingDebt ? 'Edit Debt' : 'Add Debt'}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex bg-slate-100 p-1 rounded-lg mb-4 text-center">
            <button
              type="button"
              onClick={() => setDebtType('loan')}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${debtType === 'loan' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Loan
            </button>
            <button
              type="button"
              onClick={() => setDebtType('mortgage')}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${debtType === 'mortgage' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Mortgage
            </button>
            <button
              type="button"
              onClick={() => setDebtType('credit_card')}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${debtType === 'credit_card' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Credit Card
            </button>
            <button
              type="button"
              onClick={() => setDebtType('line_of_credit')}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${debtType === 'line_of_credit' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Line of Credit
            </button>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Debt Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="e.g. Visa Credit Card"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">{(debtType === 'credit_card' || debtType === 'line_of_credit') ? 'Credit Limit / Total Available' : 'Original Loan Amount'}</label>
              <input
                type="number"
                step="0.01"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Current Amount Owed</label>
              <input
                type="number"
                step="0.01"
                value={currentAmount}
                onChange={(e) => setCurrentAmount(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder={(debtType === 'credit_card' || debtType === 'line_of_credit') ? "0.00" : "Same as total if empty"}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Monthly Minimum</label>
              <input
                type="number"
                step="0.01"
                value={monthlyMinimum}
                onChange={(e) => setMonthlyMinimum(e.target.value)}
                readOnly={debtType === 'credit_card' || debtType === 'line_of_credit'}
                className={`w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${debtType === 'credit_card' || debtType === 'line_of_credit' ? 'bg-blue-50 text-blue-700 cursor-not-allowed' : 'bg-slate-50'}`}
                placeholder="0.00"
                required
              />
              {(debtType === 'credit_card' || debtType === 'line_of_credit') && (
                <p className="text-xs text-blue-600 mt-1">Auto-calculated (Canadian standard: $10 + Interest)</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Interest Rate (%)</label>
              <input
                type="number"
                step="0.01"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="21.99"
                required
              />
            </div>
          </div>

          {(debtType === 'credit_card' || debtType === 'line_of_credit') ? (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Statement Release Date (1-31)</label>
              <div className="flex items-center gap-4">
                <input
                  type="number" min="1" max="31"
                  value={statementDate}
                  onChange={(e) => setStatementDate(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-1"
                  required
                />
              </div>
              <p className="text-xs text-slate-500 mt-1 mb-4">
                Estimated Due Date: <strong>{((Number(statementDate) || 1) + 21) % 30 || 30}th</strong> of the month (21 day grace period).
              </p>
              {debtType === 'line_of_credit' && (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="isInvestmentLoan"
                    checked={isInvestmentLoan}
                    onChange={(e) => setIsInvestmentLoan(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="isInvestmentLoan" className="text-sm font-medium text-slate-700">
                    This is a Tax-Deductible Investment Loan (e.g., Smith Maneuver)
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Payment Schedule / Frequency</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as Frequency)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-3"
              >
                <option value="monthly">Monthly (Same day every month)</option>
                <option value="semi-monthly">Semi-Monthly (Twice a month, e.g. 1st & 15th)</option>
                <option value="bi-weekly">Bi-weekly (Every 14 days)</option>
                <option value="weekly">Weekly (Every 7 days)</option>
              </select>

              {frequency === 'monthly' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Day of month:</span>
                  <input
                    type="number" min="1" max="31"
                    value={date1}
                    onChange={(e) => setDate1(e.target.value)}
                    className="w-20 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm"
                    required
                  />
                </div>
              )}

              {frequency === 'semi-monthly' && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Day 1:</span>
                    <input
                      type="number" min="1" max="31"
                      value={date1}
                      onChange={(e) => setDate1(e.target.value)}
                      className="w-16 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm"
                      required
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Day 2:</span>
                    <input
                      type="number" min="1" max="31"
                      value={date2}
                      onChange={(e) => setDate2(e.target.value)}
                      className="w-16 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm"
                      required
                    />
                  </div>
                </div>
              )}

              {(frequency === 'bi-weekly' || frequency === 'weekly') && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Next Expected Date:</span>
                  <input
                    type="date"
                    value={nextDate}
                    onChange={(e) => setNextDate(e.target.value)}
                    className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-sm"
                    required
                  />
                </div>
              )}

              {debtType === 'loan' && (
                <div className="space-y-4 pt-4 border-t border-slate-200 mt-6">
                  <h3 className="text-sm font-semibold text-slate-700">Optional Loan Details</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Total Term (Months)</label>
                      <input
                        type="number"
                        value={totalMonths}
                        onChange={(e) => setTotalMonths(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="e.g. 84"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Remaining (Months)</label>
                      <input
                        type="number"
                        value={remainingMonths}
                        onChange={(e) => setRemainingMonths(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="e.g. 49"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Issue Date</label>
                      <input
                        type="date"
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Maturity Date</label>
                      <input
                        type="date"
                        value={maturityDate}
                        onChange={(e) => setMaturityDate(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {debtType === 'mortgage' && (
                <div className="space-y-4 pt-4 border-t border-slate-200 mt-6">
                  <h3 className="text-sm font-semibold text-slate-700">Mortgage Details</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Amortization (Years)</label>
                      <input
                        type="number"
                        value={amortizationYears}
                        onChange={(e) => setAmortizationYears(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="e.g. 25"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Contract Term (Years)</label>
                      <input
                        type="number"
                        value={termYears}
                        onChange={(e) => setTermYears(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="e.g. 3"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Term Remaining (Months)</label>
                      <input
                        type="number"
                        value={remainingMonths}
                        onChange={(e) => setRemainingMonths(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="e.g. 14"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Renewal/Maturity Date</label>
                      <input
                        type="date"
                        value={maturityDate}
                        onChange={(e) => setMaturityDate(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>

                  {maturityDate && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-600 mb-1">Expected Renewal Interest Rate (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={expectedRenewalRate}
                          onChange={(e) => setExpectedRenewalRate(e.target.value)}
                          className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="e.g. 5.50"
                        />
                        <p className="text-xs text-slate-500 mt-1">AI will project using this rate starting on your renewal date.</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-600 mb-1">Current Home Value ($)</label>
                    <input
                      type="number"
                      value={homeValue}
                      onChange={(e) => setHomeValue(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Required for Debt-to-Equity (HELOC) tracking"
                    />
                    <p className="text-xs text-slate-500 mt-1">Used by the AI Advisor to project when you hit 85% DTE for the Smith Maneuver.</p>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-slate-200 mt-6">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Auto-Detect Payments (optional)</label>
                <input
                  type="text"
                  value={paymentMatchHint}
                  onChange={(e) => setPaymentMatchHint(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="e.g. mortgage, td canada trust mtg"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Comma-separated words that show up in this payment's description on your bank statement. When a linked account's transaction matches, Reconcile Statements will suggest recording it as a payment toward this debt instead of a plain expense.
                </p>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-rose-600 text-white rounded-md font-semibold text-sm hover:bg-rose-700 disabled:opacity-50 mt-4 shadow-sm transition-colors"
          >
            {loading ? 'Saving...' : (editingDebt ? 'Save Changes' : 'Add Debt')}
          </button>
        </form>
      </div>
    </div>
  );
}
